/**
 * "Your record" (WP-N4, docs/UI.md "The little black book"): everything you've done, on one page of the book.
 *
 *  - Offline it is read from the save: time on the manor, money earned, jobs done, motors nicked, the best
 *    getaway, nicked / wasted, a stamp per story job (its best time), and between jobs the races, the minicab
 *    shifts, the half-bricks and the matchbooks.
 *  - Online it is the server's word (`characterRecord`, src/online-ui/firstHour.ts) plus what the store knows
 *    about your firm: time in the city, jobs paid, money earned, motors nicked, the best getaway, times nicked,
 *    Night Freight runs and crates, your gang (rank and tier), districts your firm runs, events it won.
 *
 * Pure: builds `BookPage`s from plain data. Unit-tested in book.test.ts.
 */
import { MATCHBOOK_SPOTS } from '../activities/matchbooks';
import { MAYHEM_CHALLENGES, tierName } from '../activities/mayhem';
import type { ActivitiesSave } from '../activities/state';
import { MEDAL_NAME, formatRunTime, type JobRecord } from '../missions/medals';
import type { CharacterRecordView } from '../../shared/messages/economy';
import type { BookEntry, BookPage } from './bookScreen';

const pounds = (n: number): string => `£${Math.round(n).toLocaleString('en-GB')}`;

/** "3 h 12 min", "25 min", "under a minute". */
export function playedText(seconds: number): string {
  const m = Math.floor(Math.max(0, seconds) / 60);
  if (m < 1) return 'under a minute';
  const h = Math.floor(m / 60);
  return h ? `${h} h ${m % 60} min` : `${m} min`;
}

/** "★★★ shaken off" for a best getaway, "none yet" before the first. */
export function getawayText(level: number): string {
  const l = Math.max(0, Math.min(5, Math.round(level)));
  return l ? `${'★'.repeat(l)} shaken off` : 'none yet';
}

const cap = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);
const plural = (n: number, one: string, many = `${one}s`): string => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;

export interface OfflineRecordInput {
  stats: { playTime: number; moneyEarned: number; moneySpent: number; missionsCompleted: number; timesBusted: number; timesWasted: number; carsStolen: number; exportsDone: number; streetJobsDone: number; bestEscape: number };
  completedMissions: readonly string[];
  jobRecords: Readonly<Record<string, JobRecord>>;
  activities: ActivitiesSave;
  /** The story jobs in order (id and title). */
  story: readonly { id: string; title: string }[];
}

export function offlineRecordPage(r: OfflineRecordInput): BookPage {
  const st = r.stats;
  const storyDone = r.story.filter((m) => r.completedMissions.includes(m.id)).length;
  const MANOR = 'On the manor';
  const entries: BookEntry[] = [
    { id: 'rec:time', section: MANOR, title: 'Time on the manor', sub: playedText(st.playTime), heading: 'Time on the manor', body: [`${cap(playedText(st.playTime))} on the streets of Low Borough.`] },
    {
      id: 'rec:money',
      section: MANOR,
      title: 'Money earned',
      sub: pounds(st.moneyEarned),
      heading: 'Money earned',
      body: [`${pounds(st.moneyEarned)} earned, all told.`, `${pounds(st.moneySpent)} spent — fines, bills, motors and the rest.`],
    },
    {
      id: 'rec:jobs',
      section: MANOR,
      title: 'Jobs done',
      sub: `${storyDone} of ${r.story.length} for Mags · ${st.exportsDone} exports · ${st.streetJobsDone} street`,
      heading: 'Jobs done',
      body: [`${plural(storyDone, 'story job')} of ${r.story.length}.`, `${plural(st.exportsDone, 'export order')} for Halloran.`, `${plural(st.streetJobsDone, 'street job')} off the phone boxes.`],
    },
    { id: 'rec:stolen', section: MANOR, title: 'Motors nicked', sub: st.carsStolen.toLocaleString('en-GB'), heading: 'Motors nicked', body: [`${plural(st.carsStolen, 'motor')} taken off the street.`] },
    {
      id: 'rec:getaway',
      section: MANOR,
      title: 'Best getaway',
      sub: getawayText(st.bestEscape),
      heading: 'Best getaway',
      body: [st.bestEscape ? `The police gave up on you at ${plural(st.bestEscape, 'star')} — the most you’ve ever shaken off.` : 'You’ve not shaken the police off yet. Lose them once and it goes down here.'],
    },
    {
      id: 'rec:law',
      section: MANOR,
      title: 'Nicked and wasted',
      sub: `nicked ${st.timesBusted} · wasted ${st.timesWasted}`,
      heading: 'Nicked and wasted',
      body: [`Nicked ${plural(st.timesBusted, 'time')}, wasted ${plural(st.timesWasted, 'time')}.`],
    },
  ];
  // A stamp per story job, its best time beside it.
  for (const m of r.story) {
    const rec = r.jobRecords[m.id];
    const done = r.completedMissions.includes(m.id);
    const medal = rec?.medal ?? 0;
    entries.push({
      id: `rec:stamp:${m.id}`,
      section: 'Stamps',
      title: m.title,
      sub: medal ? `${MEDAL_NAME[medal as 1]}${rec?.best ? ` · ${formatRunTime(rec.best)}` : ''}` : done ? 'done (before stamps)' : 'not done yet',
      mark: medal === 3 ? 'proper' : medal === 2 ? 'clean' : medal === 1 || done ? 'done' : 'locked',
      heading: m.title,
      body: [
        medal ? `Best stamp: ${MEDAL_NAME[medal as 1]}${rec?.best ? `, best time ${formatRunTime(rec.best)}` : ''}.` : done ? 'Done before the stamps came in: replay it from Jobs to earn one.' : 'Not done yet.',
        ...(rec && rec.runs > 1 ? [`Played ${plural(rec.runs, 'time')}.`] : []),
      ],
    });
  }
  const a = r.activities;
  const SIDE = 'Between jobs';
  const raceBests = a.race.best.filter((t) => t > 0);
  entries.push({
    id: 'rec:race',
    section: SIDE,
    title: 'Street races',
    sub: a.race.runs ? `${a.race.wins} won of ${a.race.runs}${raceBests.length ? ` · best ${formatRunTime(Math.min(...raceBests))}` : ''}` : 'none run yet',
    heading: 'Street races',
    body: a.race.runs ? [`${plural(a.race.wins, 'win')} from ${plural(a.race.runs, 'race')}.`, ...a.race.best.map((t, i) => `· Race ${i + 1}: ${t > 0 ? formatRunTime(t) : '—'}`)] : ['No races yet.'],
  });
  const fares = a.cab.best.filter((x) => x > 0);
  entries.push({
    id: 'rec:cab',
    section: SIDE,
    title: 'Minicab shifts',
    sub: a.cab.runs ? `${plural(a.cab.runs, 'shift')}${fares.length ? ` · best ${pounds(Math.max(...fares))}` : ''}` : 'none driven yet',
    heading: 'Minicab shifts',
    body: a.cab.runs ? [`${plural(a.cab.runs, 'shift')}, ${a.cab.wins} paid in full.`, ...a.cab.best.map((p, i) => `· Shift ${i + 1}: ${p > 0 ? pounds(p) : '—'}`)] : ['No shifts yet.'],
  });
  const stamped = MAYHEM_CHALLENGES.filter((c) => (a.mayhem.tier[c.id] ?? 0) > 0).length;
  const diabolical = MAYHEM_CHALLENGES.filter((c) => (a.mayhem.tier[c.id] ?? 0) >= 3).length;
  entries.push({
    id: 'rec:bricks',
    section: SIDE,
    title: 'Half-bricks',
    sub: `${stamped} of ${MAYHEM_CHALLENGES.length} stamped${diabolical ? ` · ${diabolical} diabolical` : ''}`,
    heading: 'Half-bricks',
    body: MAYHEM_CHALLENGES.map((c) => {
      const t = a.mayhem.tier[c.id] ?? 0;
      const best = a.mayhem.best[c.id] ?? 0;
      return `· ${c.title}: ${t ? `${tierName(t)}, best ${best.toLocaleString('en-GB')}` : best ? `no stamp, best ${best.toLocaleString('en-GB')}` : 'not tried'}`;
    }),
  });
  entries.push({
    id: 'rec:matchbooks',
    section: SIDE,
    title: 'Matchbooks',
    sub: `${a.matchbooks.length} of ${MATCHBOOK_SPOTS.length} found`,
    heading: 'Matchbooks',
    body: [`${a.matchbooks.length} of ${MATCHBOOK_SPOTS.length} found. Each one pays a little, and every fourth a bit more.`],
  });
  return { heading: 'Your record', lede: 'Everything you’ve done, written down.', entries };
}

export interface OnlineRecordInput {
  rec: CharacterRecordView | null;
  gang: { name: string; tag: string; role: string; tier: number; reputation: number } | null;
}

export function onlineRecordPage(i: OnlineRecordInput): BookPage {
  const r = i.rec;
  if (!r) return { heading: 'Your record', lede: 'What the city’s books say about you.', entries: [], empty: 'Asking around… your record comes up in a moment.' };
  const CITY = 'In the city';
  const entries: BookEntry[] = [
    { id: 'rec:time', section: CITY, title: 'Time in the city', sub: playedText(r.playTimeS), heading: 'Time in the city', body: [`${cap(playedText(r.playTimeS))} on the streets of the shared city.`] },
    { id: 'rec:money', section: CITY, title: 'Money earned', sub: pounds(r.earned), heading: 'Money earned', body: [`${pounds(r.earned)} paid to you by the city, all told: jobs, sales, freight and the rest (not the money you started with).`] },
    { id: 'rec:jobs', section: CITY, title: 'Jobs done', sub: r.jobs.toLocaleString('en-GB'), heading: 'Jobs done', body: [`${plural(r.jobs, 'courier or recovery job')} paid.`] },
    { id: 'rec:stolen', section: CITY, title: 'Motors nicked', sub: r.carsStolen.toLocaleString('en-GB'), heading: 'Motors nicked', body: [`${plural(r.carsStolen, 'motor')} taken off the street.`] },
    {
      id: 'rec:getaway',
      section: CITY,
      title: 'Best getaway',
      sub: getawayText(r.bestEscape),
      heading: 'Best getaway',
      body: [r.bestEscape ? `The police gave up on you at ${plural(r.bestEscape, 'star')} — the most you’ve ever shaken off.` : 'You’ve not shaken the police off yet. Lose them once and it goes down here.'],
    },
    { id: 'rec:law', section: CITY, title: 'Times nicked', sub: r.arrests.toLocaleString('en-GB'), heading: 'Times nicked', body: [`Nicked ${plural(r.arrests, 'time')}.`] },
    {
      id: 'rec:freight',
      section: 'With your crew',
      title: 'Night Freight',
      sub: r.crewRuns ? `${plural(r.crewRuns, 'run')} · ${plural(r.crates, 'crate')}` : 'no runs yet',
      heading: 'Night Freight',
      body: [r.crewRuns ? `${plural(r.crewRuns, 'run')} with your crew, ${plural(r.crates, 'crate')} delivered.` : 'No runs yet. Night Freight is a crew run from the pier: it needs two of you.'],
    },
  ];
  const g = i.gang;
  entries.push({
    id: 'rec:gang',
    section: 'Your firm',
    title: g ? `${g.name} [${g.tag}]` : 'Gang',
    sub: g ? `${g.role} · tier ${g.tier}` : 'not in a gang',
    heading: g ? g.name : 'No gang yet',
    body: g ? [`You’re ${/^[AEIOU]/i.test(g.role) ? 'an' : 'a'} ${g.role} in ${g.name} [${g.tag}]: tier ${g.tier}, ${g.reputation.toLocaleString('en-GB')} reputation.`] : ['Not in a gang. Found one or get asked into one: the gang ledger has both.'],
  });
  entries.push({
    id: 'rec:turf',
    section: 'Your firm',
    title: 'Districts run',
    sub: r.districtsLed.toLocaleString('en-GB'),
    heading: 'Districts run',
    body: [r.districtsLed ? `Your firm runs ${plural(r.districtsLed, 'district')} right now.` : 'Your firm runs no district yet.'],
  });
  entries.push({
    id: 'rec:events',
    section: 'Your firm',
    title: 'Events won',
    sub: r.eventsWon.toLocaleString('en-GB'),
    heading: 'Events won',
    body: [`${plural(r.eventsWon, 'event')} — a boat in, a buyer, a rare motor, a contested yard — won by your firm.`],
  });
  return { heading: 'Your record', lede: 'What the city’s books say about you.', entries };
}
